import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  ShieldCheck,
  Users,
  Calendar,
  Newspaper,
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  AlertCircle,
  Loader2,
  Briefcase,
  Image,
  Camera,
  MessageSquare,
  Send,
  Smartphone,
  AtSign,
  LayoutDashboard,
  UserPlus,
  List,
  Settings,
  GraduationCap,
  Plus,
  Award,
  BookOpen, // Added for Students module
} from "lucide-react";
import Input from "../common/Input";
import Button from "../common/Button";
import toast from "react-hot-toast";

const UpdateMaintainerModal = ({ isOpen, onClose, onUpdate, user }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("Dashboard");

  // Updated modules array with Students module
  const modules = [
    { key: "Dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "Admin Portal Users", label: "Admin Portal Users", icon: UserPlus },
    { key: "Jobs", label: "Jobs", icon: Briefcase },
    { key: "Gallery", label: "Gallery", icon: Image },
    { key: "News", label: "News", icon: Newspaper },
    { key: "Events", label: "Events", icon: Calendar },
    { key: "Members", label: "Members", icon: Users },
    { key: "Designation List", label: "Designation List", icon: List },
    { key: "Leadership Role", label: "Leadership Role", icon: Settings },
    { key: "Photo Slogan", label: "Photo Slogan", icon: Camera },
    { key: "Newsletter Hub", label: "Newsletter Hub", icon: Send },
    { key: "Communication", label: "Communication", icon: MessageSquare },
    { key: "Education", label: "Education", icon: GraduationCap },
    { key: "Degree", label: "Degree", icon: Award },
    { key: "Students", label: "Students", icon: BookOpen }, // New Students module
  ];

  const standardPermissions = [
    { key: "view", label: "View", icon: Eye, color: "text-blue-600" },
    { key: "edit", label: "Edit", icon: Edit, color: "text-green-600" },
    { key: "delete", label: "Delete", icon: Trash2, color: "text-red-600" },
  ];

  const communicationPermissions = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageSquare,
      color: "text-green-600",
    },
    { key: "sms", label: "SMS", icon: Smartphone, color: "text-blue-600" },
    { key: "email", label: "Email", icon: AtSign, color: "text-purple-600" },
  ];

  const designationPermissions = [
    {
      key: "view",
      label: "View Designations",
      icon: Eye,
      color: "text-blue-600",
    },
    {
      key: "create",
      label: "Create Designation",
      icon: UserPlus,
      color: "text-green-600",
    },
    {
      key: "edit",
      label: "Edit Designation",
      icon: Edit,
      color: "text-yellow-600",
    },
  ];

  const leadershipPermissions = [
    {
      key: "view",
      label: "View Leadership Roles",
      icon: Eye,
      color: "text-blue-600",
    },
    { key: "edit", label: "Edit Role", icon: Edit, color: "text-yellow-600" },
    {
      key: "delete",
      label: "Delete Role",
      icon: Trash2,
      color: "text-red-600",
    },
  ];

  const educationPermissions = [
    {
      key: "view",
      label: "View Education",
      icon: Eye,
      color: "text-blue-600",
    },
    {
      key: "edit",
      label: "Edit Education",
      icon: Edit,
      color: "text-yellow-600",
    },
    {
      key: "delete",
      label: "Delete Education",
      icon: Trash2,
      color: "text-red-600",
    },
  ];

  const degreePermissions = [
    {
      key: "view",
      label: "View Degrees",
      icon: Eye,
      color: "text-blue-600",
    },
    {
      key: "edit",
      label: "Edit Degree",
      icon: Edit,
      color: "text-yellow-600",
    },
    {
      key: "delete",
      label: "Delete Degree",
      icon: Trash2,
      color: "text-red-600",
    },
  ];

  // Students specific permissions - New module
  const studentsPermissions = [
    {
      key: "view",
      label: "View Students",
      icon: Eye,
      color: "text-blue-600",
    },
    {
      key: "edit",
      label: "Edit Students",
      icon: Edit,
      color: "text-yellow-600",
    },
    {
      key: "delete",
      label: "Delete Students",
      icon: Trash2,
      color: "text-red-600",
    },
  ];

  const [permissions, setPermissions] = useState([]);

  const resetPermissions = () => {
    return modules.map((module) => {
      if (module.key === "Dashboard") {
        return {
          module: module.key,
          view: true,
          edit: false,
          delete: false,
        };
      } else if (module.key === "Communication") {
        return {
          module: module.key,
          whatsapp: false,
          sms: false,
          email: false,
        };
      } else if (module.key === "Designation List") {
        return {
          module: module.key,
          view: false,
          create: false,
          edit: false,
        };
      } else if (module.key === "Leadership Role") {
        return {
          module: module.key,
          view: false,
          edit: false,
          delete: false,
        };
      } else if (module.key === "Education") {
        return {
          module: module.key,
          view: true,
          edit: false,
          delete: false,
        };
      } else if (module.key === "Degree") {
        return {
          module: module.key,
          view: false,
          edit: false,
          delete: false,
        };
      } else if (module.key === "Students") {
        // Students reset
        return {
          module: module.key,
          view: false,
          edit: false,
          delete: false,
        };
      } else {
        return {
          module: module.key,
          view: false,
          edit: false,
          delete: false,
        };
      }
    });
  };

  useEffect(() => {
    if (isOpen && user) {
      console.log("Populating user data:", user);

      setFirstName(user.fullName || "");
      setEmail(user.email || "");
      setChangePassword(false);
      setPassword("");
      setError("");
      setActiveTab("Dashboard");

      if (user.permissions && user.permissions.length > 0) {
        const updatedPermissions = modules.map((module) => {
          const existingPerm = user.permissions.find(
            (p) => p.module === module.key
          );

          if (existingPerm) {
            const cleanPerm = {
              module: module.key,
              view: existingPerm.view || false,
              edit: existingPerm.edit || false,
              delete: existingPerm.delete || false,
              whatsapp: existingPerm.whatsapp || false,
              sms: existingPerm.sms || false,
              email: existingPerm.email || false,
              create: existingPerm.create || false,
              assign: existingPerm.assign || false,
            };

            if (module.key === "Degree") {
              const oldEducationPerm = user.permissions.find(
                (p) => p.module === "Education"
              );
              if (oldEducationPerm && oldEducationPerm.manage_degree) {
                cleanPerm.view = true;
                cleanPerm.edit = true;
                cleanPerm.delete = true;
              }
            }

            return cleanPerm;
          }

          if (module.key === "Dashboard") {
            return {
              module: module.key,
              view: true,
              edit: false,
              delete: false,
            };
          } else if (module.key === "Communication") {
            return {
              module: module.key,
              whatsapp: false,
              sms: false,
              email: false,
            };
          } else if (
            module.key === "Designation List" ||
            module.key === "Leadership Role"
          ) {
            return {
              module: module.key,
              view: false,
              edit: false,
              delete: false,
            };
          } else if (module.key === "Education") {
            return {
              module: module.key,
              view: true,
              edit: false,
              delete: false,
            };
          } else if (module.key === "Degree") {
            return {
              module: module.key,
              view: false,
              edit: false,
              delete: false,
            };
          } else if (module.key === "Students") {
            // Students initialization
            return {
              module: module.key,
              view: false,
              edit: false,
              delete: false,
            };
          } else {
            return {
              module: module.key,
              view: false,
              edit: false,
              delete: false,
            };
          }
        });

        setPermissions(updatedPermissions);
      } else {
        setPermissions(resetPermissions());
      }
    }
  }, [isOpen, user]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handlePermissionToggle = (moduleKey, permissionType) => {
    setPermissions((prev) => {
      return prev.map((perm) => {
        if (perm.module === moduleKey) {
          if (moduleKey === "Communication") {
            return {
              ...perm,
              [permissionType]: !perm[permissionType],
            };
          } else if (
            moduleKey === "Designation List" ||
            moduleKey === "Leadership Role"
          ) {
            if (permissionType === "view") {
              return {
                ...perm,
                view: !perm.view,
                edit: !perm.view ? false : perm.edit,
                delete: !perm.view ? false : perm.delete,
              };
            } else {
              return {
                ...perm,
                view: true,
                [permissionType]: !perm[permissionType],
              };
            }
          } else if (moduleKey === "Education") {
            if (permissionType === "view") {
              return {
                ...perm,
                view: !perm.view,
                add: !perm.view ? false : perm.add,
                edit: !perm.view ? false : perm.edit,
                delete: !perm.view ? false : perm.delete,
              };
            } else {
              return {
                ...perm,
                view: true,
                [permissionType]: !perm[permissionType],
              };
            }
          } else if (moduleKey === "Degree") {
            if (permissionType === "view") {
              return {
                ...perm,
                view: !perm.view,
                add: !perm.view ? false : perm.add,
                edit: !perm.view ? false : perm.edit,
                delete: !perm.view ? false : perm.delete,
              };
            } else {
              return {
                ...perm,
                view: true,
                [permissionType]: !perm[permissionType],
              };
            }
          } else if (moduleKey === "Students") {
            // Students toggle handling
            if (permissionType === "view") {
              return {
                ...perm,
                view: !perm.view,
                edit: !perm.view ? false : perm.edit,
                delete: !perm.view ? false : perm.delete,
              };
            } else {
              return {
                ...perm,
                view: true,
                [permissionType]: !perm[permissionType],
              };
            }
          } else {
            if (permissionType === "view") {
              return {
                ...perm,
                view: !perm.view,
                edit: !perm.view ? false : perm.edit,
                delete: !perm.view ? false : perm.delete,
              };
            } else {
              return {
                ...perm,
                view: true,
                [permissionType]: !perm[permissionType],
              };
            }
          }
        }
        return perm;
      });
    });
  };

  const hasPermission = (moduleKey, permissionType) => {
    const modulePermission = permissions.find((p) => p.module === moduleKey);
    return modulePermission?.[permissionType] || false;
  };

  const getActivePermissionsCount = () => {
    return permissions.filter((perm) => {
      if (perm.module === "Communication") {
        return perm.whatsapp || perm.sms || perm.email;
      } else if (
        perm.module === "Designation List" ||
        perm.module === "Leadership Role"
      ) {
        return perm.view || perm.create || perm.edit || perm.delete;
      } else if (perm.module === "Education") {
        return perm.view || perm.edit || perm.delete;
      } else if (perm.module === "Degree") {
        return perm.view || perm.edit || perm.delete;
      } else if (perm.module === "Students") {
        // Students active count
        return perm.view || perm.edit || perm.delete;
      } else {
        return perm.view || perm.edit || perm.delete;
      }
    }).length;
  };

  const handleSubmit = async () => {
    if (!firstName || !email) {
      setError("Name and email are required.");
      return;
    }

    if (changePassword && (!password || password.length < 6)) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    const activePermissions = permissions.filter((perm) => {
      if (perm.module === "Communication") {
        return perm.whatsapp || perm.sms || perm.email;
      } else if (
        perm.module === "Designation List" ||
        perm.module === "Leadership Role"
      ) {
        return (
          perm.view || perm.create || perm.edit || perm.delete || perm.assign
        );
      } else if (perm.module === "Education") {
        return perm.view || perm.edit || perm.delete;
      } else if (perm.module === "Degree") {
        return perm.view || perm.edit || perm.delete;
      } else if (perm.module === "Students") {
        // Students validation
        return perm.view || perm.edit || perm.delete;
      } else {
        return perm.view || perm.edit || perm.delete;
      }
    });

    if (activePermissions.length === 0) {
      setError("Please assign at least one permission to the maintainer.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const updateData = {
        fullName: firstName,
        email,
        permissions: activePermissions,
      };

      if (changePassword && password) {
        updateData.password = password;
      }

      await onUpdate(user._id, updateData);
      toast.success("Maintainer updated successfully!");
      resetForm();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFirstName("");
    setEmail("");
    setPassword("");
    setChangePassword(false);
    setError("");
    setActiveTab("Dashboard");
    setPermissions(resetPermissions());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center rounded-2xl justify-center p-4">
        <div
          className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl flex flex-col"
          style={{ maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header - Fixed */}
          <div className="flex-shrink-0 flex items-center rounded-2xl justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#4f22d8] rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Update Maintainer
                </h1>
                <p className="text-gray-600 text-sm mt-1">
                  Modify maintainer details and permissions
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Modal Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                    <p className="text-red-700 text-sm font-medium">{error}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Basic Information */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Basic Information Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <User className="w-5 h-5 text-[#4f22d8]" />
                      <span className="text-lg font-semibold text-gray-900">
                        Basic Information
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <User className="w-4 h-4" />
                        </div>
                        <Input
                          label="User Name"
                          value={firstName}
                          onChange={setFirstName}
                          required
                          placeholder="Enter maintainer name"
                          disabled={loading}
                          className="pl-10"
                        />
                      </div>

                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <Mail className="w-4 h-4" />
                        </div>
                        <Input
                          label="Email Address"
                          type="email"
                          value={email}
                          onChange={setEmail}
                          required
                          placeholder="Enter email address"
                          disabled={loading}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Permission Management */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-5 h-5 text-[#4f22d8]" />
                        <div>
                          <span className="text-lg font-semibold text-gray-900">
                            Module Permissions
                          </span>
                          <p className="text-sm text-gray-600">
                            Configure access permissions for different modules
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#4f22d8]/10 text-[#4f22d8]">
                          {getActivePermissionsCount()} active
                        </span>
                      </div>
                    </div>

                    {/* Tab Navigation - Updated grid to accommodate Students module */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 p-1 bg-gray-100 rounded-xl mb-6">
                      {modules.map((module) => {
                        const modulePerms = permissions.find(
                          (p) => p.module === module.key
                        );
                        const hasActivePerms =
                          module.key === "Communication"
                            ? modulePerms?.whatsapp ||
                              modulePerms?.sms ||
                              modulePerms?.email
                            : module.key === "Designation List" ||
                              module.key === "Leadership Role"
                            ? modulePerms?.view ||
                              modulePerms?.edit ||
                              modulePerms?.delete ||
                              modulePerms?.assign
                            : module.key === "Education"
                            ? modulePerms?.view ||
                              modulePerms?.edit ||
                              modulePerms?.delete
                            : module.key === "Degree"
                            ? modulePerms?.view ||
                              modulePerms?.edit ||
                              modulePerms?.delete
                            : module.key === "Students"
                            ? modulePerms?.view ||
                              modulePerms?.edit ||
                              modulePerms?.delete
                            : modulePerms?.view ||
                              modulePerms?.edit ||
                              modulePerms?.delete;
                        const IconComponent = module.icon;

                        return (
                          <button
                            key={module.key}
                            onClick={() => setActiveTab(module.key)}
                            disabled={loading}
                            className={`flex flex-col items-center gap-1 py-2 px-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                              activeTab === module.key
                                ? "bg-[#4f22d8] text-white shadow-lg"
                                : "text-gray-600 hover:text-gray-900 hover:bg-white"
                            }`}
                          >
                            <IconComponent className="w-4 h-4" />
                            <span className="text-center leading-tight">
                              {module.label}
                            </span>
                            {hasActivePerms && (
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  activeTab === module.key
                                    ? "bg-white"
                                    : "bg-green-500"
                                }`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Permission Options for Active Tab - Updated with Students */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-[#4f22d8]/10 rounded-lg flex items-center justify-center">
                          {React.createElement(
                            modules.find((m) => m.key === activeTab)?.icon,
                            {
                              className: "w-5 h-5 text-[#4f22d8]",
                            }
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-xl text-gray-900">
                            {modules.find((m) => m.key === activeTab)?.label}{" "}
                            Permissions
                          </h3>
                          <p className="text-sm text-gray-600">
                            {activeTab === "Communication"
                              ? "Configure communication channel access"
                              : activeTab === "Designation List"
                              ? "Configure designation management permissions"
                              : activeTab === "Leadership Role"
                              ? "Configure leadership role management permissions"
                              : activeTab === "Education"
                              ? "Configure education management permissions"
                              : activeTab === "Degree"
                              ? "Configure degree management permissions with separate controls"
                              : activeTab === "Students"
                              ? "Configure student management permissions (view, edit, delete)"
                              : "Configure what actions are allowed"}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {(() => {
                          let permissionsToShow;
                          if (activeTab === "Communication") {
                            permissionsToShow = communicationPermissions;
                          } else if (activeTab === "Designation List") {
                            permissionsToShow = designationPermissions;
                          } else if (activeTab === "Leadership Role") {
                            permissionsToShow = leadershipPermissions;
                          } else if (activeTab === "Education") {
                            permissionsToShow = educationPermissions;
                          } else if (activeTab === "Degree") {
                            permissionsToShow = degreePermissions;
                          } else if (activeTab === "Students") {
                            // Students permissions
                            permissionsToShow = studentsPermissions;
                          } else {
                            permissionsToShow = standardPermissions;
                          }

                          return permissionsToShow.map((permType) => {
                            const isActive = hasPermission(
                              activeTab,
                              permType.key
                            );
                            const isDisabled =
                              activeTab !== "Communication" &&
                              activeTab !== "Designation List" &&
                              activeTab !== "Leadership Role" &&
                              activeTab !== "Education" &&
                              activeTab !== "Degree" &&
                              activeTab !== "Students" &&
                              permType.key !== "view" &&
                              !hasPermission(activeTab, "view");
                            const IconComponent = permType.icon;

                            return (
                              <label
                                key={permType.key}
                                className={`flex items-center gap-4 p-4 bg-white border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                  isActive
                                    ? "border-[#4f22d8] shadow-md"
                                    : "border-gray-200 hover:border-gray-300"
                                } ${
                                  isDisabled
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <div
                                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                      isActive
                                        ? "bg-[#4f22d8] border-[#4f22d8]"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {isActive && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>

                                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <IconComponent
                                      className={`w-4 h-4 ${permType.color}`}
                                    />
                                  </div>

                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-gray-900">
                                        {permType.label}
                                      </span>
                                      {activeTab !== "Communication" &&
                                        activeTab !== "Designation List" &&
                                        activeTab !== "Leadership Role" &&
                                        activeTab !== "Education" &&
                                        activeTab !== "Degree" &&
                                        activeTab !== "Students" &&
                                        permType.key === "view" && (
                                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                            Required
                                          </span>
                                        )}
                                      {activeTab === "Students" &&
                                        permType.key === "view" && (
                                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                            Required
                                          </span>
                                        )}
                                      {(activeTab === "Education" ||
                                        activeTab === "Degree") &&
                                        permType.key === "view" && (
                                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                            {activeTab === "Education"
                                              ? "Default"
                                              : "Required"}
                                          </span>
                                        )}
                                    </div>
                                    {isDisabled && (
                                      <span className="text-xs text-red-600 font-medium">
                                        View permission required
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <input
                                  type="checkbox"
                                  checked={isActive}
                                  onChange={() => {
                                    if (!isDisabled) {
                                      handlePermissionToggle(
                                        activeTab,
                                        permType.key
                                      );
                                    }
                                  }}
                                  disabled={isDisabled || loading}
                                  className="!sr-only"
                                />
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Summary - Updated with Students */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 space-y-6">
                    {/* Summary Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Summary
                      </h3>

                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-600" />
                          <span className="text-sm text-gray-600">Type:</span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            Maintainer
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">
                              Active Permissions
                            </span>
                            <span className="text-xs bg-[#4f22d8]/10 text-[#4f22d8] px-2 py-1 rounded-full">
                              {getActivePermissionsCount()} modules
                            </span>
                          </div>

                          {getActivePermissionsCount() > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {permissions
                                .filter((perm) => {
                                  if (perm.module === "Communication") {
                                    return (
                                      perm.whatsapp || perm.sms || perm.email
                                    );
                                  } else if (
                                    perm.module === "Designation List" ||
                                    perm.module === "Leadership Role"
                                  ) {
                                    return (
                                      perm.view ||
                                      perm.edit ||
                                      perm.delete ||
                                      perm.assign
                                    );
                                  } else if (perm.module === "Education") {
                                    return (
                                      perm.view ||
                                      perm.add ||
                                      perm.edit ||
                                      perm.delete
                                    );
                                  } else if (perm.module === "Degree") {
                                    return (
                                      perm.view ||
                                      perm.add ||
                                      perm.edit ||
                                      perm.delete
                                    );
                                  } else if (perm.module === "Students") {
                                    // Students filtering in summary
                                    return (
                                      perm.view || perm.edit || perm.delete
                                    );
                                  } else {
                                    return (
                                      perm.view || perm.edit || perm.delete
                                    );
                                  }
                                })
                                .map((perm) => {
                                  const module = modules.find(
                                    (m) => m.key === perm.module
                                  );
                                  const IconComponent = module?.icon;

                                  let activePerms = [];
                                  if (perm.module === "Communication") {
                                    if (perm.whatsapp)
                                      activePerms.push("WhatsApp");
                                    if (perm.sms) activePerms.push("SMS");
                                    if (perm.email) activePerms.push("Email");
                                  } else if (
                                    perm.module === "Designation List" ||
                                    perm.module === "Leadership Role"
                                  ) {
                                    if (perm.view) activePerms.push("View");
                                    if (perm.edit) activePerms.push("Edit");
                                    if (perm.delete) activePerms.push("Delete");
                                    if (perm.assign) activePerms.push("Assign");
                                  } else if (perm.module === "Education") {
                                    if (perm.view) activePerms.push("View");
                                    if (perm.add) activePerms.push("Add");
                                    if (perm.edit) activePerms.push("Edit");
                                    if (perm.delete) activePerms.push("Delete");
                                  } else if (perm.module === "Degree") {
                                    if (perm.view) activePerms.push("View");
                                    if (perm.add) activePerms.push("Add");
                                    if (perm.edit) activePerms.push("Edit");
                                    if (perm.delete) activePerms.push("Delete");
                                  } else if (perm.module === "Students") {
                                    // Students active permissions display
                                    if (perm.view) activePerms.push("View");
                                    if (perm.edit) activePerms.push("Edit");
                                    if (perm.delete) activePerms.push("Delete");
                                  } else {
                                    if (perm.view) activePerms.push("View");
                                    if (perm.edit) activePerms.push("Edit");
                                    if (perm.delete) activePerms.push("Delete");
                                  }

                                  return (
                                    <div
                                      key={perm.module}
                                      className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                                    >
                                      <IconComponent className="w-3 h-3 text-gray-600 flex-shrink-0" />
                                      <span className="text-xs font-medium text-gray-700 flex-1 truncate">
                                        {module?.label}
                                      </span>
                                      <div className="flex flex-wrap gap-1">
                                        {activePerms.map((p) => (
                                          <span
                                            key={p}
                                            className="bg-[#4f22d8]/10 text-[#4f22d8] px-1 py-0.5 rounded text-xs"
                                          >
                                            {p}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                              <p className="text-red-600 font-medium text-sm">
                                No permissions assigned
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer - Fixed */}
          <div className="flex-shrink-0 flex rounded-b-2xl items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              className="px-6 py-2 bg-[#4f22d8] hover:bg-[#4f22d8]/90 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Update Maintainer
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateMaintainerModal;
